import React, { useMemo, useState, useEffect } from 'react';
import { Shift } from '../types';
import { calculateHoursBreakdown } from '../services/calculationService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { Wallet, DollarSign, Calendar, ShoppingCart, Clock, TrendingUp, Users, Share2, FileText, ChevronRight, X, PiggyBank, Briefcase, Info, Loader2, Download, Copy, Sun, Coins, Moon, Percent, Rocket, Target, Coffee, AlertTriangle, Eye, EyeOff, Headset, BookOpen, Volume2, MessageSquare, XCircle, AlertCircle, Globe } from 'lucide-react';
import { format, getMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { WeatherWidget } from './WeatherWidget';
import { supabase } from '../supabaseClient';
import { DriverScore } from './DriverScore';
import { ShiftCountdown } from './ShiftCountdown';
import { UserProfile } from '../types';

interface DashboardProps {
    shifts: Shift[];
    isLoading?: boolean;
    onAdvanceClick?: () => void;
    onPlanWeek?: () => void;
    isPrivacyMode: boolean;
    setIsPrivacyMode: (val: boolean) => void;
    userProfile: UserProfile | null;
    tourStep: number;
    setTourStep: (val: number | ((prev: number) => number)) => void;
    isPremiumOrVip?: boolean;
}

// Helpers to replace date-fns functions that are failing to import
const getStartOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1) - day; // Adjust for Monday start (1)
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
};

const getEndOfWeek = (date: Date) => {
    const d = getStartOfWeek(date);
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
};

const FleetCount = ({ total }: { total: number }) => (
    <div className="flex items-center gap-2 bg-slate-900/50 px-3 py-1.5 rounded-full border border-slate-700/50 self-start md:self-auto animate-fade-in shadow-sm">
        <Users className="w-4 h-4 text-blue-400" />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden sm:inline">Comunidade:</span>
        <span className="text-xs font-black text-white">{total} motoristas</span>
    </div>
);

export const Dashboard: React.FC<DashboardProps> = ({
    shifts,
    isLoading = false,
    onAdvanceClick,
    onPlanWeek,
    isPrivacyMode,
    setIsPrivacyMode,
    userProfile,
    tourStep,
    setTourStep,
    isPremiumOrVip = false
}: DashboardProps) => {
    const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
    const [isSharingInvite, setIsSharingInvite] = useState(false);
    const [isSharingReport, setIsSharingReport] = useState(false);
    const [updateTourStep, setUpdateTourStep] = useState<number>(0);

    // --- RHINO BONUS CALCULATIONS ---
    const rhinoBonusInfo = useMemo(() => {
        const today = new Date();
        const currentMonthStr = format(today, 'yyyy-MM');
        const monthShifts = shifts.filter(s => s.date.startsWith(currentMonthStr) && s.type === 'WORK');
        const noShowCount = shifts.filter(s => s.date.startsWith(currentMonthStr) && (s.type === 'NO_SHOW' || s.noShow)).length;

        let delaysAbove10Min = 0;
        const lateDates: string[] = [];

        const toMin = (t: string) => { const [h, mi] = t.split(':').map(Number); return h * 60 + mi; };

        monthShifts.forEach(s => {
            const pStart = s.plannedStartTime || s.startTime;
            const plannedStartMin = toMin(pStart);
            const realStartMin = toMin(s.startTime);
            const carArrivalMin = s.carArrivalTime ? toMin(s.carArrivalTime) : 0;
            const referenceStartMin = Math.max(plannedStartMin, carArrivalMin);
            
            const lateRaw = Math.max(0, realStartMin - referenceStartMin);
            if (lateRaw > 10) {
                delaysAbove10Min++;
                lateDates.push(format(new Date(s.date + 'T00:00:00'), "dd/MM"));
            }
        });

        const isDisqualified = delaysAbove10Min >= 2 || noShowCount > 0;
        const warningType = delaysAbove10Min === 1 && noShowCount === 0 ? 'WARNING' : (isDisqualified ? 'DANGER' : 'SAFE');

        return {
            delaysAbove10Min,
            lateDates,
            noShowCount,
            isDisqualified,
            warningType
        };
    }, [shifts]);
    
    // Privacy state now handled via props
    const [isRhinoFilter, setIsRhinoFilter] = useState(false); // New Filter: Geral vs Apenas Rhino
    const [isWeekBannerDismissed, setIsWeekBannerDismissed] = useState(false);

    // Auto-scroll to details on mobile when a month is selected
    useEffect(() => {
        if (selectedMonth) {
            const el = document.getElementById('month-details-container');
            if (el && window.innerWidth < 1024) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
            }
        }
    }, [selectedMonth]);

    // State synced from App.tsx via props

    const formatValue = (value: number | string) => {
        if (isPrivacyMode) return '****';
        if (typeof value === 'number') return `R$ ${value.toFixed(2)}`;
        return value;
    };

    // Report Modal State
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportMonth, setReportMonth] = useState<string>('');
    const [reportBonusToggle, setReportBonusToggle] = useState(true);
    const [reportManualDiscount, setReportManualDiscount] = useState<string>('0');
    const [reportAppTipsManual, setReportAppTipsManual] = useState<string>('0'); // New Manual App Tips

    // Community Counter State
    const [totalDrivers, setTotalDrivers] = useState<number>(0);

    // Tour State now comes from props

    useEffect(() => {
        // Check Tour Status
        const hasSeenTour = localStorage.getItem('driver_score_tour_completed');
        const isTourEnabled = localStorage.getItem('driver_score_tour_enabled') !== 'false';
        if (!hasSeenTour && isTourEnabled && !isLoading && shifts.length === 0) {
            // Delay slightly to ensure UI renders
            setTimeout(() => setTourStep(1), 1000);
        }
    }, [isLoading]);

    useEffect(() => {
        const hasSeenInitial = localStorage.getItem('driver_score_tour_completed') === 'true';
        const hasSeenUpdate = localStorage.getItem('driver_score_v1_13_tour_completed') === 'true';
        const isTourActive = tourStep > 0;
        
        if (hasSeenInitial && !hasSeenUpdate && !isTourActive && !isLoading) {
            const timer = setTimeout(() => {
                setUpdateTourStep(1);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [isLoading, tourStep]);

    useEffect(() => {
        // --- Vibe Coding Mode: ON ⚡ ---
        const fetchCount = async () => {
            try {
                console.log('🚀 Iniciando contagem da frota...');

                // Tentamos buscar na tabela de usuários real. 
                // DICA: Se o nome for diferente de 'users', é só trocar ali embaixo!
                // Method 3: Most robust - fetch IDs and count in JS if exact count headers fail
                const { data, error } = await supabase
                    .from('profiles')
                    .select('*')
                    .limit(1);

                if (error) {
                    console.warn('⚠️ Erro ao buscar IDs dos perfis:', error);
                    setTotalDrivers(80);
                    return;
                }

                if (data && data.length > 0) {
                    console.log('🔍 DEBUG: Primeiro perfil encontrado:', data[0]);
                    const { count, error: countErr } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
                    if (!countErr && count) setTotalDrivers(count);
                    else setTotalDrivers(data.length); // Fallback to at least something
                } else {
                    setTotalDrivers(80);
                }
            } catch (e) {
                console.error('❌ Erro na conexão. Mantendo contagem manual.', e);
                setTotalDrivers(80);
            }
        };

        fetchCount();
        // --- Vibe Coding Mode: OFF ---
    }, [isLoading]);

    // Initialize Report Month
    useEffect(() => {
        if (!reportMonth) {
            setReportMonth(format(new Date(), 'yyyy-MM'));
        }
    }, []);

    const tourContent = [
        { title: '', text: '' }, // Step 0 placeholder
        {
            title: 'Bem-vindo ao Driver Score! 🚀',
            text: 'Este é o seu novo painel de controle financeiro. Vamos fazer um tour rápido pelas novidades e funções do app?',
            target: 'CENTER'
        },
        {
            title: 'A Grande Jornada dos 22 Tiers! 🏆',
            text: 'Temos um sistema de ranking reformulado com 22 níveis incríveis que vão do Ferro até o elo máximo de 1 Milhão de pontos (Singularidade)! Poste no Marketplace ou complete turnos para ganhar pontos e subir!',
            target: 'CENTER'
        },
        {
            title: 'Comunidade em Tempo Real 👥',
            text: 'Aqui você vê quantos motoristas estão conectados no Driver Score hoje. Juntos somos mais fortes!',
            target: 'tour-driver-count'
        },
        {
            title: 'Resumo Financeiro 📊',
            text: 'Estes cards cobrem seu saldo, ganhos e gastos de forma rápida e clara. Dica: as restrições para usuários gratuitos foram completamente removidas!',
            target: 'tour-kpis'
        },
        {
            title: 'Calculadora de Adiantamento 💰',
            text: 'Recurso Premium! Calcule com exatidão quanto você pode adiantar da sua quinzena de forma independente.',
            target: 'tour-advance'
        },
        {
            title: 'Gráfico de Desempenho 📈',
            text: 'Acompanhe sua evolução financeira mensal. Clique nas barras para ver detalhes de cada mês.',
            target: 'tour-chart'
        },
        {
            title: 'Detalhamento do Mês 🔍',
            text: 'Aqui você vê a quebra de ganhos, horas noturnas e adicionais detalhadamente, incluindo auditoria de descansos liberada para todos!',
            target: 'tour-breakdown'
        },
        {
            title: 'Histórico Completo ⏱️',
            text: 'No histórico você gerencia seus turnos passados. Adicione quantos turnos quiser por dia, sem nenhum limite no plano gratuito!',
            target: 'tour-history'
        },
        {
            title: 'Marketplace e Stories Livres! 🛒',
            text: 'Divulgue produtos e serviços ou compre de colegas. O Stories é 100% grátis e ilimitado para divulgar e farmar pontos a qualquer momento!',
            target: 'tour-marketplace'
        },
        {
            title: 'Zere seu Ano! 🎁',
            text: 'Indique amigos! Se você for assinante ativo e o indicado também assinar, ambos ganham 3 meses de isenção total! Indique 4 colegas ativos e ZERE SEU ANO (12 meses grátis)!',
            target: 'CENTER'
        },
        {
            title: 'Dica de Ouro: PWA! 📱',
            text: 'Instale o Driver Score na sua tela inicial para ter a experiência completa de um app nativo.',
            target: 'CENTER'
        }
    ];

    const handleNextTourStep = () => {
        if (tourStep === 5) {
            onAdvanceClick?.(); // Trigger view change to Finance
            setTourStep(6);
        } else if (tourStep < tourContent.length - 1) {
            setTourStep(prev => prev + 1);
        } else {
            finishTour();
        }
    };

    const finishTour = () => {
        setTourStep(0);
        localStorage.setItem('driver_score_tour_completed', 'true');
    };

    const parseDate = (str: string) => {
        if (!str) return new Date();
        const cleanStr = str.substring(0, 10);

        // Support YYYY-MM-DD
        if (cleanStr.includes('-') && cleanStr.indexOf('-') === 4) {
            const [y, m, d] = cleanStr.split('-').map(Number);
            return new Date(y, m - 1, d);
        }

        // Support DD/MM/YYYY or DD-MM-YYYY
        if (cleanStr.includes('/') || (cleanStr.includes('-') && cleanStr.indexOf('-') === 2)) {
            const separator = cleanStr.includes('/') ? '/' : '-';
            const [d, m, y] = cleanStr.split(separator).map(Number);
            if (y > 31) { // Basic check for year position
                return new Date(y, m - 1, d);
            }
        }

        // Fallback to native parsing but it's dangerous for regional formats
        const d = new Date(cleanStr);
        return isNaN(d.getTime()) ? new Date() : d;
    };

    // Available Months for Report
    const availableMonths = useMemo(() => {
        const months = new Set<string>();
        shifts.forEach(shift => {
            if (shift.date && shift.date.length >= 10) {
                months.add(shift.date.substring(0, 7));
            }
        });
        // Ensure current month is available even if no shifts
        months.add(format(new Date(), 'yyyy-MM'));
        return Array.from(months).sort().reverse();
    }, [shifts]);

    // Aggregate Data
    const stats = useMemo(() => {
        const now = new Date();
        const currentMonthStr = format(now, 'yyyy-MM');

        // Total Lifetime Values
        let totalRhinoEarnings = 0;
        let totalExtraEarnings = 0;
        let totalExpenses = 0;
        let totalHours = 0;

        // Weekly Stats (Mon-Sun)
        let currentWeekEarnings = 0;
        const weekStart = getStartOfWeek(now); // Monday
        const weekEnd = getEndOfWeek(now);

        shifts.forEach(s => {
            // Filter logic for lifetime/weekly if RhinoFilter is active
            const isRhinoShift = true; // Shifts themselves (WORK/OFF/NO_SHOW) are usually RHINO origin implicitly, but we could check if needed.

            // Lifetime Earnings Calculation
            if (s.type === 'WORK') {
                // Legacy support for 'tips' field (treating it as outsideTips if appTips/outsideTips are missing)
                const sAppTips = s.appTips || 0;
                const sOutsideTips = s.outsideTips || (s.appTips === undefined ? (s as any).tips || 0 : 0);

                const totalDailyRhino = (s.totalEarnings || 0) + sAppTips;

                if (!isRhinoFilter) {
                    totalRhinoEarnings += totalDailyRhino + sOutsideTips;
                } else {
                    totalRhinoEarnings += totalDailyRhino;
                }

                totalHours += (s.totalHours || 0);
            }

            if (s.extraEarnings) {
                const filteredExtras = isRhinoFilter
                    ? s.extraEarnings.filter(e => e.origin === 'RHINO')
                    : s.extraEarnings;
                totalExtraEarnings += filteredExtras.reduce((a, b) => a + b.amount, 0);
            }

            if (s.expenses) {
                const filteredExpenses = isRhinoFilter
                    ? s.expenses.filter(e => e.origin === 'RHINO')
                    : s.expenses;
                totalExpenses += filteredExpenses.reduce((a, b) => a + b.amount, 0);
            }

            // Weekly Calculation
            const shiftDate = parseDate(s.date);
            if (shiftDate >= weekStart && shiftDate <= weekEnd) {
                const sAppTips = s.appTips || 0;
                const sOutsideTips = s.outsideTips || (s.appTips === undefined ? (s as any).tips || 0 : 0);

                if (s.type === 'WORK') {
                    currentWeekEarnings += (s.totalEarnings || 0) + sAppTips;
                    if (!isRhinoFilter) currentWeekEarnings += sOutsideTips;
                }

                if (s.extraEarnings) {
                    const filteredExtras = isRhinoFilter
                        ? s.extraEarnings.filter(e => e.origin === 'RHINO')
                        : s.extraEarnings;
                    currentWeekEarnings += filteredExtras.reduce((a, b) => a + b.amount, 0);
                }

                if (s.expenses) {
                    const filteredExpenses = isRhinoFilter
                        ? s.expenses.filter(e => e.origin === 'RHINO')
                        : s.expenses;
                    currentWeekEarnings -= filteredExpenses.reduce((a, b) => a + b.amount, 0);
                }
            }
        });

        // Lucro Real (Real Profit) Logic - Independent of Filter state
        // Total Gains (all origins) - Expenses(Personal) - Expenses(Fuel)
        // Reset and start fresh every month (only count current month's shifts)
        let lucroRealGlobal = 0;
        shifts.forEach(s => {
            if (!s.date || s.date.substring(0, 7) !== currentMonthStr) return;
            const sAppTips = s.appTips || 0;
            const sOutsideTips = s.outsideTips || (s.appTips === undefined ? (s as any).tips || 0 : 0);
            lucroRealGlobal += (s.totalEarnings || 0) + sAppTips + sOutsideTips;

            if (s.extraEarnings) lucroRealGlobal += s.extraEarnings.reduce((a, b) => a + b.amount, 0);
            if (s.expenses) {
                const detrimentalExpenses = s.expenses
                    .filter(e => e.origin === 'PERSONAL' || e.category === 'FUEL')
                    .reduce((a, b) => a + b.amount, 0);
                lucroRealGlobal -= detrimentalExpenses;
            }
        });

        const totalNetBalance = (totalRhinoEarnings + totalExtraEarnings) - totalExpenses;
        const totalGrossLifetime = totalRhinoEarnings + totalExtraEarnings;

        // Current Month Stats
        const currentMonthShiftsAllTypes = shifts.filter(s => {
            if (!s.date || s.date.length < 7) return false;
            return s.date.substring(0, 7) === currentMonthStr;
        });

        const currentMonthWorkShifts = currentMonthShiftsAllTypes.filter(s => s.type === 'WORK');

        // Calculations for Current Month
        let currentMonthRhinoBase = 0; // Pure calculated earnings
        let currentMonthTips = 0; // Pure tips
        let currentMonthExtra = 0;
        let currentMonthExpenses = 0;
        let currentMonthBase = 0;
        let currentMonthNight = 0;
        let currentMonthBilingual = 0;
        let currentMonthDayHours = 0;
        let currentMonthNightHours = 0;
        let currentMonthFees = 0;

        currentMonthWorkShifts.forEach(s => {
            const sAppTips = s.appTips || 0;
            const sOutsideTips = s.outsideTips || (s.appTips === undefined ? (s as any).tips || 0 : 0);

            currentMonthRhinoBase += (s.totalEarnings || 0);
            currentMonthTips += sAppTips;
            if (!isRhinoFilter) currentMonthTips += sOutsideTips;

            currentMonthBase += (s.breakdown?.basePay || 0);
            currentMonthNight += (s.breakdown?.nightPay || 0);
            currentMonthBilingual += (s.breakdown?.bilingualPay || 0);

            const { dayHours, nightHours } = calculateHoursBreakdown(s.date, s.startTime, s.endTime);
            currentMonthDayHours += dayHours;
            currentMonthNightHours += nightHours;
        });

        currentMonthShiftsAllTypes.forEach(s => {
            if (s.extraEarnings) {
                const filteredExtras = isRhinoFilter
                    ? s.extraEarnings.filter(e => e.origin === 'RHINO')
                    : s.extraEarnings;
                currentMonthExtra += filteredExtras.reduce((a, b) => a + b.amount, 0);
            }

            if (s.expenses) {
                const filteredExpenses = isRhinoFilter
                    ? s.expenses.filter(e => e.origin === 'RHINO')
                    : s.expenses;

                const totalExp = filteredExpenses.reduce((a, b) => a + b.amount, 0);
                currentMonthExpenses += totalExp;

                // Track fees specifically (Fees are always Rhino origin)
                const fees = s.expenses
                    .filter(e => e.description?.includes('Taxa Plific') || e.description?.includes('Custo de Adiantamento'))
                    .reduce((a, b) => a + b.amount, 0);
                currentMonthFees += fees;
            }
        });

        const currentMonthTotalRhino = currentMonthRhinoBase + currentMonthTips;
        const currentMonthNet = (currentMonthTotalRhino + currentMonthExtra) - currentMonthExpenses;

        // Counters
        const daysOffCount = currentMonthShiftsAllTypes.filter(s => s.type === 'OFF').length;
        const noShowCount = currentMonthShiftsAllTypes.filter(s => s.type === 'NO_SHOW' || s.noShow).length;

        // Monthly Data for Chart (Stacked)
        const monthlyData: Record<string, { rhino: number; extra: number; expenses: number; sortKey: string }> = {};

        shifts.forEach(shift => {
            const date = parseDate(shift.date);
            const rawMonthName = format(date, 'MMM/yy', { locale: ptBR });
            const displayMonth = rawMonthName.charAt(0).toUpperCase() + rawMonthName.slice(1);
            const sortKey = format(date, 'yyyy-MM');

            if (!monthlyData[displayMonth]) {
                monthlyData[displayMonth] = { rhino: 0, extra: 0, expenses: 0, sortKey };
            }

            if (shift.type === 'WORK') {
                const sAppTips = shift.appTips || 0;
                const sOutsideTips = shift.outsideTips || (shift.appTips === undefined ? (shift as any).tips || 0 : 0);

                monthlyData[displayMonth].rhino += (shift.totalEarnings || 0) + sAppTips;
                if (!isRhinoFilter) monthlyData[displayMonth].rhino += sOutsideTips;
            }
            if (shift.extraEarnings) {
                const filteredExtras = isRhinoFilter
                    ? shift.extraEarnings.filter(e => e.origin === 'RHINO')
                    : shift.extraEarnings;
                monthlyData[displayMonth].extra += filteredExtras.reduce((a, b) => a + b.amount, 0);
            }
            if (shift.expenses) {
                const filteredExpenses = isRhinoFilter
                    ? shift.expenses.filter(e => e.origin === 'RHINO')
                    : shift.expenses;
                monthlyData[displayMonth].expenses += filteredExpenses.reduce((a, b) => a + b.amount, 0);
            }
        });

        const chartData = Object.entries(monthlyData)
            .map(([name, data]) => ({ name, ...data }))
            .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

        return {
            totalNetBalance,
            totalGrossLifetime,
            totalRhinoEarnings,
            totalHours,
            chartData,
            currentMonthNet,
            currentWeekEarnings,
            currentMonthRhinoTotal: currentMonthTotalRhino, // Base + Tips
            currentMonthRhinoBase, // Pure Base
            currentMonthExtra,
            currentMonthExpenses,
            currentMonthTips,
            currentMonthBase,
            currentMonthNight,
            currentMonthBilingual,
            currentMonthNightHours,
            currentMonthDayHours,
            currentMonthFees,
            currentMonthShifts: currentMonthShiftsAllTypes,
            daysOffCount,
            noShowCount,
            lucroRealGlobal
        };
    }, [shifts]);

    const selectedShifts = useMemo(() => {
        if (!selectedMonth) return [];
        return shifts.filter(shift => {
            const date = parseDate(shift.date);
            const rawMonthName = format(date, 'MMM/yy', { locale: ptBR });
            const displayMonth = rawMonthName.charAt(0).toUpperCase() + rawMonthName.slice(1);
            return displayMonth === selectedMonth;
        }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [shifts, selectedMonth]);

    // --- BOTÃO 1: CONVITE ---
    const handleInvite = async () => {
        setIsSharingInvite(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            let userName = user?.user_metadata?.full_name || 'Um colega';
            
            // Use userProfile.referral_code, fallback to short ID if not available yet
            const referralCode = userProfile?.referral_code || (user?.id ? user.id.substring(0, 8).toUpperCase() : 'anon');
            const inviteLink = `${window.location.origin}?ref=${referralCode}&name=${encodeURIComponent(userName)}`;
            
            const shareText = `Fala parceiro! Tô usando o Driver Score pra organizar meus ganhos e não deixar dinheiro na mesa com os multiplicadores. 🚀\n\nAchei sensacional e usei o app pra te mandar esse convite. Se você entrar por esse link e assinar, nós dois ganhamos 3 MESES DE GRAÇA na hora! 🎁\n\nClica aí e dá uma olhada: ${inviteLink}`;
            
            window.alert("🎁 BÔNUS DE INDICAÇÃO!\n\nSe o seu amigo assinar pelo seu link, ambos ganham 3 MESES GRÁTIS (máximo 4 indicações)!");
            
            if (navigator.share) await navigator.share({ title: 'Driver Score', text: shareText, url: inviteLink });
            else await navigator.clipboard.writeText(shareText);
        } catch (error) { console.log('Error sharing', error); } finally { setIsSharingInvite(false); }
    };

    // --- BOTÃO 2: ESPELHO RH (MODAL HANDLER) ---
    const handleGenerateMirror = async () => {
        if (!reportMonth) return;
        setIsSharingReport(true);

        try {
            const { data: { user } = {} } = await supabase.auth.getUser(); // Destructure with default empty object
            let userName = user?.user_metadata?.full_name || 'Motorista';

            // Filter Logic
            const shiftsToReport = shifts.filter(s => s.date.startsWith(reportMonth));

            // Format Month Name nicely
            const [y, m] = reportMonth.split('-').map(Number);
            const dateObj = new Date(y, m - 1);
            const periodName = format(dateObj, 'MMMM yyyy', { locale: ptBR });
            const capitalizedPeriod = periodName.charAt(0).toUpperCase() + periodName.slice(1);

            const workShifts = shiftsToReport.filter(s => s.type === 'WORK');
            const noShowCount = shiftsToReport.filter(s => s.type === 'NO_SHOW' || s.noShow).length;

            const reportHours = workShifts.reduce((acc, s) => acc + (s.totalHours || 0), 0);
            const reportPerformed = workShifts.reduce((acc, s) => acc + (s.breakdown?.basePay || 0) + (s.breakdown?.nightPay || 0), 0);
            const reportBilingual = workShifts.reduce((acc, s) => acc + (s.breakdown?.bilingualPay || 0), 0);

            const manualAppTips = parseFloat(reportAppTipsManual) || 0;

            const valorAntecipado = shiftsToReport.reduce((acc, s) => {
                if (!s.expenses) return acc;
                const fees = s.expenses
                    .filter(e => e.description?.includes('Taxa Plific') || e.description?.includes('Custo de Adiantamento'))
                    .reduce((a, b) => a + b.amount, 0);
                return acc + fees;
            }, 0);

            // --- AUDIT CALCULATIONS (Rhino Policy) ---
            const toMin = (t: string) => { const [h, mi] = t.split(':').map(Number); return h * 60 + mi; };

            let totalLateValue = 0;
            let totalEarlyDepartureValue = 0;
            let totalBreakBonus = 0;
            let delaysAbove10Min = 0;
            const lateDates: string[] = [];

            workShifts.forEach(s => {
                // Only compute for adjusted shifts (those with plannedStartTime)
                const pStart = s.plannedStartTime || s.startTime;
                const pEnd = s.plannedEndTime || s.endTime;

                const plannedStartMin = toMin(pStart);
                const plannedEndMin = toMin(pEnd);
                const realStartMin = toMin(s.startTime);
                const realEndMin = toMin(s.endTime);

                // LATE ARRIVAL (Policy #1): Tolerance 5 min, then x2
                // New Rule: Reference = max(Planned, CarArrival)
                const carArrivalMin = s.carArrivalTime ? toMin(s.carArrivalTime) : 0;
                const referenceStartMin = Math.max(plannedStartMin, carArrivalMin);
                
                const lateRaw = Math.max(0, realStartMin - referenceStartMin);
                const lateMinutes = lateRaw > 5 ? lateRaw : 0;

                if (lateMinutes > 0) {
                    const lateCentesimal = (lateMinutes / 60) * 100;
                    totalLateValue += lateCentesimal * 0.40; // x2
                }

                // Track delays > 10 min for bonus eligibility (based on reference)
                if (lateRaw > 10) {
                    delaysAbove10Min++;
                    lateDates.push(format(parseDate(s.date), "dd/MM"));
                }

                // EARLY DEPARTURE (Policy #2): x2
                let earlyDepartureMin = 0;
                const logoutTimeStr = s.logoutTelegramTime || s.endTime;
                if (logoutTimeStr) {
                    const logoutMin = toMin(logoutTimeStr);
                    if (plannedEndMin < plannedStartMin) {
                        const pEndAdj = plannedEndMin + 1440;
                        const tEndAdj = logoutMin <= realStartMin ? logoutMin + 1440 : logoutMin;
                        earlyDepartureMin = Math.max(0, pEndAdj - tEndAdj);
                    } else {
                        earlyDepartureMin = Math.max(0, plannedEndMin - logoutMin);
                    }
                }

                if (earlyDepartureMin > 0) {
                    const edCentesimal = (earlyDepartureMin / 60) * 100;
                    totalEarlyDepartureValue += edCentesimal * 0.40; // x2
                }

                // BREAK BONUS (Policy #3): Unused break at x1
                const breakMin = s.unpaidBreakMinutes || 0;
                if (breakMin > 0) {
                    const breakCentesimal = (breakMin / 60) * 100;
                    totalBreakBonus += breakCentesimal * 0.20; // x1
                }
            });

            // BONUS ELIGIBILITY: 10% on performed, but disqualified if 2+ delays > 10min or noShows
            const potentialBonus = reportPerformed * 0.10;
            const bonusEligible = delaysAbove10Min < 2 && noShowCount === 0;
            const bonusValue = reportBonusToggle && bonusEligible ? potentialBonus : 0;

            const lastDayOfMonth = new Date(y, m, 0).getDate();
            const monthNameOnly = periodName.split(' ')[0];
            const nfValue = reportPerformed + reportBilingual + bonusValue + manualAppTips;
            const totalDebits = valorAntecipado + parseFloat(reportManualDiscount || '0') + totalLateValue + totalEarlyDepartureValue;
            const totalAdditionals = manualAppTips + totalBreakBonus + reportBilingual + bonusValue;
            const netReceive = nfValue - valorAntecipado - parseFloat(reportManualDiscount || '0') - totalLateValue - totalEarlyDepartureValue + totalBreakBonus;

            let bonusStatus = 'NÃO';
            if (reportBonusToggle && bonusEligible) {
                bonusStatus = 'SIM';
            } else if (reportBonusToggle && !bonusEligible) {
                bonusStatus = `NÃO (${delaysAbove10Min >= 2 ? delaysAbove10Min + ' atrasos >10min' : noShowCount + ' falta(s)'})`; 
            }

            const reportText = `📋 Espelho de Horas — ${capitalizedPeriod}

--

🧾 Valor da Nota Fiscal: R$ ${nfValue.toFixed(2).replace('.', ',')}

⏱️ Horas Trabalhadas: ${reportHours.toFixed(2).replace('.', ',')}

💰 Valor Performado: R$ ${reportPerformed.toFixed(2).replace('.', ',')}

💰 Tem bônus? ${bonusStatus}

--

🔻 Débitos:
- Valor Antecipado: R$ ${valorAntecipado.toFixed(2).replace('.', ',')}
- No-show: R$ ${parseFloat(reportManualDiscount || '0').toFixed(2).replace('.', ',')}
- Login com atraso: R$ -${totalLateValue.toFixed(2).replace('.', ',')}
- Logoff antecipado: R$ -${totalEarlyDepartureValue.toFixed(2).replace('.', ',')}
- Multa de trânsito: R$ 0,00
 - Débitos de mês passado: R$ -

--

➕ Adicionais:
- Gorjeta: R$ ${manualAppTips.toFixed(2).replace('.', ',')}
- Pausa: R$ ${totalBreakBonus.toFixed(2).replace('.', ',')}
- Atendimento Bilíngue: R$ ${reportBilingual.toFixed(2).replace('.', ',')}
- Bônus: R$ ${bonusValue.toFixed(2).replace('.', ',')}

--

📅 Período: 01 a ${lastDayOfMonth} de ${monthNameOnly.toLowerCase()}

--
🧾 Valor da Nota Fiscal: R$ ${nfValue.toFixed(2).replace('.', ',')}

✅ Valor a Receber: R$ ${netReceive.toFixed(2).replace('.', ',')}

--
${!bonusEligible ? `⚠️ AVISO: Bônus de 10% perdido por ${delaysAbove10Min >= 2 ? `excesso de atrasos (>10min): ${lateDates.join(', ')}` : `ausência (No-show)`}.` : ''}
`;

            if (navigator.share) {
                await navigator.share({ title: `Espelho RH - ${capitalizedPeriod} `, text: reportText });
            } else {
                await navigator.clipboard.writeText(reportText);
                alert("Relatório copiado para a área de transferência!");
            }

            setShowReportModal(false); // Close after success

        } catch (err) {
            console.error("Error generating report", err);
            alert("Erro ao compartilhar. Tente novamente.");
        } finally {
            setIsSharingReport(false);
        }
    };

    const StatCard = ({ title, value, sub, icon: Icon, color, isHighlight = false, subColor = "text-slate-500" }: any) => (
        <div className={`${isHighlight ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-orange-500/50' : 'bg-slate-800 border-slate-700'} p-6 rounded-xl border shadow-lg relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:border-slate-500/50 group`}>
            <div className="flex items-start justify-between relative z-10">
                <div>
                    <p className={`${isHighlight ? 'text-orange-400' : 'text-slate-400'} text-sm font-medium mb-1 uppercase tracking-wide`}>{title}</p>
                    <h3 className="text-2xl font-bold text-white">{value}</h3>
                    {sub && <p className={`text-xs mt-1 ${subColor}`}>{sub}</p>}
                </div>
                <div className={`p-3 rounded-lg ${color} transition-transform duration-300 group-hover:rotate-6`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
            </div>
        </div>
    );

    const BreakdownBar = ({ label, value, total, color, icon: Icon, tooltip, subLabel, valueFormatter }: any) => {
        const percentage = total > 0 ? (value / total) * 100 : 0;
        const displayValue = valueFormatter ? valueFormatter(value) : `R$ ${value.toFixed(2)}`;

        return (
            <div className="mb-4 last:mb-0">
                <div className="flex justify-between items-end mb-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-300 flex items-center gap-2"><Icon className="w-4 h-4 text-slate-500" /> {label}</span>
                        {tooltip && <Info className="w-3 h-3 text-slate-600" />}
                    </div>
                    <div className="text-right">
                        <span className="font-bold text-white block">{displayValue}</span>
                        {subLabel && <span className="text-[10px] text-slate-500">{subLabel}</span>}
                    </div>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2.5">
                    <div className={`h-2.5 rounded-full ${color}`} style={{ width: `${percentage}%` }}></div>
                </div>
            </div>
        );
    };

    const isThriftyDay = (shift: Shift) => {
        if (!shift.expenses) return true; // No expenses = thrifty
        const dailyEssentialExpenses = shift.expenses
            .filter(e => e.category === 'FOOD' || e.category === 'TRANSPORT')
            .reduce((sum, e) => sum + e.amount, 0);
        return dailyEssentialExpenses < 50;
    };

    const TourOverlay = () => {
        if (tourStep === 0) return null;

        const currentContent = tourContent[tourStep];
        const isLastStep = tourStep === tourContent.length - 1;

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in">
                {/* Backdrop */}
                <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={finishTour}></div>

                {/* Tooltip Card */}
                <div className={`relative z-[110] bg-slate-800 border border-orange-500/50 p-6 rounded-2xl shadow-2xl max-w-sm w-full mx-4 ${tourStep === 1 ? 'scale-100' : 'translate-y-20 md:translate-y-0'}`}>
                    <div className="flex justify-between items-start mb-4">
                        <div className="bg-orange-600 p-3 rounded-full shadow-lg shadow-orange-900/40 -mt-10 mb-2 border-4 border-slate-900">
                            {tourStep === 1 ? <Rocket className="w-8 h-8 text-white" /> : <Target className="w-8 h-8 text-white" />}
                        </div>
                        <button onClick={finishTour} className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <h3 className="text-xl font-bold text-white mb-2">{currentContent.title}</h3>
                    <p className="text-slate-300 text-sm leading-relaxed mb-6">{currentContent.text}</p>

                    <div className="flex items-center justify-between">
                        <div className="flex gap-1.5 max-w-[50%] flex-wrap">
                            {Array.from({ length: tourContent.length - 1 }, (_, i) => i + 1).map(s => (
                                <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s === tourStep ? 'w-5 bg-orange-500' : 'w-1.5 bg-slate-600'}`} />
                            ))}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <button
                                onClick={handleNextTourStep}
                                className="bg-white text-slate-900 hover:bg-slate-200 px-5 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors shadow-lg active:scale-95 whitespace-nowrap"
                            >
                                {isLastStep ? 'Concluir' : 'Próximo'} <ChevronRight className="w-4 h-4" />
                            </button>
                            <button
                                onClick={finishTour}
                                className="text-slate-500 hover:text-slate-400 text-[10px] underline cursor-pointer"
                            >
                                Pular Tour
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const updateTourContent = [
        { title: '', text: '', icon: '' }, // Step 0 placeholder
        {
            title: '☀️ Light Mode & Dark Mode 🌙',
            text: 'Agora você pode escolher a aparência do aplicativo! Alternar para o modo Claro melhora a visibilidade na luz do dia, enquanto o modo Escuro protege sua visão à noite.',
            icon: '🎨'
        },
        {
            title: '⚽ Copa do Mundo em Campo! 🏆',
            text: 'O tema Copa traz as cores do Brasil, textura de gramado de futebol, confetes verde e amarelo e apito de juiz sintetizado de verdade ao finalizar seus turnos e metas!',
            icon: '⚽'
        },
        {
            title: '⚙️ Alterne Facilmente 📲',
            text: 'Escolha seu tema preferido nas configurações de "Meu Perfil" ou use o botão de atalho rápido (🌙 / ☀️ / ⚽) no cabeçalho. Experimente agora!',
            icon: '✨'
        }
    ];

    const UpdateTourOverlay = () => {
        if (updateTourStep === 0) return null;
        const current = updateTourContent[updateTourStep];
        const isLast = updateTourStep === updateTourContent.length - 1;

        return (
            <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
                <div className="bg-slate-800 border border-orange-500/50 p-6 rounded-2xl shadow-2xl max-w-sm w-full mx-4 animate-slide-up relative flex flex-col items-center text-center">
                    {updateTourStep === 2 && (
                        <div className="absolute -top-12 -left-6 text-4xl animate-bounce">⚽</div>
                    )}
                    {updateTourStep === 2 && (
                        <div className="absolute -top-6 -right-6 text-4xl animate-bounce" style={{ animationDelay: '0.2s' }}>🇧🇷</div>
                    )}

                    <div className="text-[10px] uppercase tracking-wider font-bold text-orange-400 bg-orange-400/10 px-3 py-1 rounded-full mb-4">
                        Novidade v1.13 • Passo {updateTourStep} de 3
                    </div>

                    <div className="text-5xl mb-4 animate-pulse">{current.icon}</div>

                    <h3 className="text-lg font-black text-white mb-3 tracking-tight">
                        {current.title}
                    </h3>

                    <p className="text-sm text-slate-300 leading-relaxed mb-6">
                        {current.text}
                    </p>

                    <div className="w-full flex gap-3">
                        <button
                            onClick={() => {
                                setUpdateTourStep(0);
                                localStorage.setItem('driver_score_v1_13_tour_completed', 'true');
                            }}
                            className="flex-1 bg-slate-900 border border-slate-700 hover:bg-slate-850 text-slate-400 hover:text-slate-200 py-3 rounded-xl transition-all font-bold text-sm min-h-[44px]"
                        >
                            Pular
                        </button>
                        <button
                            onClick={() => {
                                if (isLast) {
                                    setUpdateTourStep(0);
                                    localStorage.setItem('driver_score_v1_13_tour_completed', 'true');
                                    import('../services/theme-effects').then(m => {
                                        m.playWhistle();
                                        m.triggerConfetti();
                                    });
                                } else {
                                    setUpdateTourStep(prev => prev + 1);
                                }
                            }}
                            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-3 rounded-xl transition-all font-bold text-sm shadow-lg shadow-orange-950/20 active:scale-95 flex items-center justify-center gap-1.5 min-h-[44px]"
                        >
                            {isLast ? 'Experimentar! 🎉' : 'Avançar'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-slate-700 rounded w-48 mb-4"></div>
                <section id="tour-kpis" className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-slate-800 rounded-xl"></div>)}
                </section>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in flex flex-col h-full relative">
            <TourOverlay />
            <UpdateTourOverlay />

            {userProfile?.is_bilingual_active && (
                <div className="bg-blue-900/40 border border-blue-500/50 p-4 rounded-xl flex items-center justify-between animate-fade-in shadow-lg">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg">
                            <Globe className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-white font-bold text-sm">Serviço Bilíngue Ativo</h3>
                            <p className="text-blue-200 text-xs mt-0.5 max-w-[200px] sm:max-w-full">
                                Você está isento de bater o ponto na garagem.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={async () => {
                            try {
                                await supabase.from('profiles').update({ is_bilingual_active: false }).eq('id', userProfile.id);
                                alert('Serviço Bilíngue encerrado! Lembre-se de fazer login no App e Telegram para voltar à fila de despacho.');
                                window.location.reload();
                            } catch (e) {
                                console.error(e);
                            }
                        }}
                        className="bg-blue-600 hover:bg-blue-500 text-white text-[10px] sm:text-xs font-bold px-3 py-2 rounded-lg transition-colors shadow-md shrink-0"
                    >
                        Encerrar
                    </button>
                </div>
            )}

            {/* Header with Weather & Fleet Count */}
            <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
                <div className="flex-1 flex flex-col md:flex-row md:items-center gap-4">
                    <WeatherWidget />
                    {totalDrivers > 0 && (
                        <div id="tour-driver-count">
                            <FleetCount total={totalDrivers} />
                        </div>
                    )}
                </div>
            </div>

            {/* SEGREGATION FILTER */}
            <div className="flex bg-slate-900/50 p-1 rounded-xl border border-slate-700 w-full max-w-sm">
                <button
                    onClick={() => setIsRhinoFilter(false)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all ${!isRhinoFilter ? 'bg-slate-700 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    visão Geral
                </button>
                <button
                    onClick={() => setIsRhinoFilter(true)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all ${isRhinoFilter ? 'bg-orange-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    <Briefcase className="w-4 h-4" /> Apenas Rhino
                </button>
            </div>

            {/* SMART BANNER: Empty Week Alert */}
            {(() => {
                if (isWeekBannerDismissed) return null;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const next7 = new Date(today);
                next7.setDate(next7.getDate() + 7);
                const hasUpcomingShifts = shifts.some(s => {
                    if (!s.date) return false;
                    const sd = new Date(s.date + 'T00:00:00');
                    return sd >= today && sd <= next7;
                });
                if (hasUpcomingShifts) return null;
                return (
                    <div className="relative overflow-hidden bg-gradient-to-r from-red-900/60 via-orange-900/40 to-amber-900/30 border border-red-500/40 p-5 rounded-2xl shadow-xl shadow-red-900/10 animate-fade-in">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full -translate-y-8 translate-x-8" />
                        <button onClick={() => setIsWeekBannerDismissed(true)} className="absolute top-3 right-3 p-1 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors z-10">
                            <X className="w-4 h-4" />
                        </button>
                        <div className="flex items-start gap-4 relative z-[1]">
                            <div className="bg-red-600/30 p-3 rounded-xl border border-red-500/30 flex-shrink-0 mt-0.5">
                                <AlertTriangle className="w-6 h-6 text-red-400 animate-pulse" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-white font-black text-sm mb-1.5 leading-tight">🚨 Escala de hoje ainda não cadastrada!</h4>
                                <p className="text-red-200/80 text-xs leading-relaxed mb-4">Insira seu turno agora para não perder o controle dos atrasos <span className="text-red-300 font-bold">(x2)</span> e garantir seus bônus de descanso.</p>
                                <button
                                    onClick={onPlanWeek}
                                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition-all shadow-lg shadow-orange-900/30 active:scale-95 flex items-center gap-2"
                                >
                                    <Calendar className="w-4 h-4" />
                                    Planejar Minha Semana
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}



            {/* RHINO BONUS AUDIT BANNER */}
            {rhinoBonusInfo.warningType !== 'SAFE' && (
                <div className={`relative overflow-hidden p-5 rounded-2xl border shadow-xl animate-fade-in ${
                    rhinoBonusInfo.warningType === 'DANGER' 
                        ? 'bg-red-900/30 border-red-500/40 text-red-100' 
                        : 'bg-amber-900/30 border-amber-500/40 text-amber-100'
                }`}>
                    <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl border flex-shrink-0 ${
                            rhinoBonusInfo.warningType === 'DANGER' 
                                ? 'bg-red-600/20 border-red-500/30 text-red-400' 
                                : 'bg-amber-600/20 border-amber-500/30 text-amber-400'
                        }`}>
                            {rhinoBonusInfo.warningType === 'DANGER' ? <XCircle className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
                        </div>
                        <div className="flex-1">
                            <h4 className="font-bold text-sm mb-1 uppercase tracking-wider">
                                {rhinoBonusInfo.warningType === 'DANGER' ? '🚫 Bônus Mensal Perdido' : '⚠️ Atenção ao Bônus'}
                            </h4>
                            <p className="text-xs opacity-90 leading-relaxed">
                                {rhinoBonusInfo.warningType === 'DANGER' 
                                    ? `Você perdeu o bônus de 10% este mês devido a ${rhinoBonusInfo.noShowCount > 0 ? 'falta (no-show)' : `excesso de atrasos: ${rhinoBonusInfo.lateDates.join(', ')}`}.`
                                    : `Você já usou 1 tolerância de atraso (${rhinoBonusInfo.lateDates[0]}). No próximo atraso acima de 10min, você perderá o bônus de 10% do mês.`
                                }
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-slate-800/50 p-6 rounded-2xl border border-slate-700/50 gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-orange-600/20 p-3 rounded-xl shadow-lg shadow-orange-600/10">
                        <DollarSign className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-white">Saldo Disponível</h2>
                            <button
                                onClick={() => setIsPrivacyMode(!isPrivacyMode)}
                                className="p-1 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                            >
                                {isPrivacyMode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        <p className="text-sm text-slate-400">Total acumulado bruto</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                    <button
                        id="tour-advance"
                        onClick={onAdvanceClick}
                        className="w-full flex items-center justify-between p-3.5 bg-orange-600 hover:bg-orange-500 text-white rounded-xl transition-all shadow-lg shadow-orange-900/40 relative overflow-hidden group"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/10 rounded-lg">
                                <Wallet className="w-5 h-5" />
                            </div>
                            <div className="text-left">
                                <div className="text-xs font-medium text-orange-200 uppercase tracking-wider">Serviço Financeiro</div>
                                <div className="text-sm font-black flex items-center gap-2">
                                    Simular Adiantamento
                                </div>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-orange-200 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <div className="flex gap-2 w-full md:w-auto">
                        <div className="flex-1 md:flex-none bg-slate-900 px-6 py-2.5 rounded-xl border border-slate-700 flex items-center justify-center min-w-[140px]">
                            <span className="text-2xl font-black text-white">{formatValue(stats.totalGrossLifetime)}</span>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => (window as any).dispatchAlarm?.()}
                                className="flex items-center justify-center bg-slate-800 text-slate-300 w-12 h-12 rounded-xl border border-slate-700 active:scale-95 transition-transform"
                                title="Testar Despertador"
                            >
                                <Volume2 className="w-5 h-5 text-indigo-400" />
                            </button>
                            <button
                                onClick={handleInvite}
                                disabled={isSharingInvite}
                                className="flex items-center justify-center bg-slate-800 text-slate-300 w-12 h-12 rounded-xl border border-slate-700 active:scale-95 transition-transform"
                                title="Convidar Motorista"
                            >
                                {isSharingInvite ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5 text-blue-400" />}
                            </button>
                            <button
                                onClick={() => setShowReportModal(true)}
                                className="flex items-center justify-center bg-slate-800 text-slate-300 w-12 h-12 rounded-xl border border-slate-700 active:scale-95 transition-transform"
                                title="Gerar Espelho"
                            >
                                <FileText className="w-5 h-5 text-green-400" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Abertura de Escala Countdown */}
            {(() => {
                const scoreToUse = userProfile?.rhino_score ?? userProfile?.xp_points ?? 100;
                const currentTier = scoreToUse >= 95 ? 'Ouro' : scoreToUse >= 80 ? 'Prata' : 'Bronze';

                return (
                    <ShiftCountdown
                        tierLabel={currentTier}
                    />
                );
            })()}

            {/* KPI CARDS */}
            <div id="tour-kpis" className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 transition-all duration-500 ${tourStep === 4 ? 'relative z-[105] ring-4 ring-orange-500 rounded-xl bg-slate-900/50 p-2 -m-2' : ''}`}>
                <StatCard
                    title="Saldo Líquido (Mês)"
                    value={formatValue(stats.currentMonthNet)}
                    sub="O que sobra no bolso"
                    subColor={stats.currentMonthNet >= 0 ? 'text-green-400' : 'text-red-400'}
                    icon={Wallet}
                    color={stats.currentMonthNet >= 0 ? 'bg-blue-600' : 'bg-red-600'}
                    isHighlight={true}
                />
                <StatCard
                    title="Semana Atual"
                    value={formatValue(stats.currentWeekEarnings)}
                    sub="Segunda a Domingo"
                    subColor={stats.currentWeekEarnings > 0 ? 'text-green-400' : 'text-slate-500'}
                    icon={Calendar}
                    color="bg-purple-600"
                />
                <StatCard
                    title="Gastos Pessoais"
                    value={formatValue(stats.currentMonthExpenses)}
                    sub="Alimentação, Combustível, etc"
                    subColor="text-red-400"
                    icon={ShoppingCart}
                    color="bg-red-500"
                />
                <StatCard
                    title="Dias Trabalhados"
                    value={stats.currentMonthShifts.filter(s => s.type === 'WORK').length}
                    sub={`${stats.currentMonthDayHours.toFixed(0)}h Diurnas | ${stats.currentMonthNightHours.toFixed(0)}h Noturnas`}
                    icon={Clock}
                    color="bg-slate-600"
                />
            </div>

            {/* English Academy preview card was removed */}

            {/* LUCRO REAL BANNER */}
            <div className="bg-gradient-to-r from-emerald-900/40 to-slate-900 border border-emerald-500/30 p-6 rounded-2xl shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 transition-all hover:border-emerald-500/50 group">
                <div className="flex items-center gap-5">
                    <div className="bg-emerald-600 p-4 rounded-2xl shadow-lg shadow-emerald-900/40 group-hover:scale-110 transition-transform">
                        <TrendingUp className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h4 className="text-emerald-400 font-black text-xs uppercase tracking-[0.2em] mb-1">Lucro Real do Mês</h4>
                        <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-black text-white">{formatValue(stats.lucroRealGlobal)}</span>
                            <span className="text-emerald-500 text-sm font-bold animate-pulse">Livre de Combustível/Casa</span>
                        </div>
                    </div>
                </div>
                <div className="hidden md:block bg-emerald-900/20 px-4 py-2 rounded-lg border border-emerald-800/50">
                    <p className="text-[10px] text-emerald-300 leading-tight">Cálculo: (Ganhos Totais) - (Gastos Casa) - (Combustível)</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6 flex flex-col">
                    {/* CHART */}
                    <div id="tour-chart" className={`bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-lg flex flex-col h-96 transition-all duration-500 ${tourStep === 6 ? 'relative z-[105] ring-4 ring-orange-500 scale-[1.02]' : ''}`}>
                        <div className="flex items-center gap-2 mb-6">
                            <h3 className="text-lg font-semibold text-white">Evolução Financeira</h3>
                            <div className="text-xs text-slate-500 ml-auto flex gap-3">
                                <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500"></span> Ganhos</div>
                                <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500"></span> Gastos</div>
                            </div>
                        </div>
                        <div className="w-full flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats.chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                    <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} tickLine={false} axisLine={false} />
                                    <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(value) => `R$${value}`} />
                                    <Tooltip
                                        cursor={{ fill: '#334155', opacity: 0.2 }}
                                        contentStyle={{ backgroundColor: '#1e293b', borderColor: '#475569', color: '#fff', borderRadius: '8px' }}
                                        itemStyle={{ color: '#e2e8f0', fontWeight: 'bold' }}
                                        formatter={(value: number, name: string) => [
                                            formatValue(value),
                                            name === 'rhino' ? 'Rhino + Tips' : name === 'extra' ? 'Renda Extra' : 'Gastos'
                                        ]}
                                    />
                                    <Bar dataKey="rhino" stackId="a" radius={[0, 0, 4, 4]} onClick={(data) => setSelectedMonth(data.name === selectedMonth ? null : data.name)} cursor="pointer">
                                        {stats.chartData.map((entry: any, index: number) => (
                                            <Cell
                                                key={`rhino-${index}`}
                                                fill={selectedMonth ? (entry.name === selectedMonth ? '#4ade80' : '#22c55e40') : '#22c55e'}
                                                stroke={entry.name === selectedMonth ? '#fff' : 'none'}
                                                strokeWidth={entry.name === selectedMonth ? 2 : 0}
                                                style={{ transition: 'all 0.3s ease', filter: entry.name === selectedMonth ? 'drop-shadow(0 0 8px rgba(74, 222, 128, 0.6))' : 'none' }}
                                            />
                                        ))}
                                    </Bar>
                                    <Bar dataKey="extra" stackId="a" radius={[4, 4, 0, 0]} onClick={(data) => setSelectedMonth(data.name === selectedMonth ? null : data.name)} cursor="pointer">
                                        {stats.chartData.map((entry: any, index: number) => (
                                            <Cell
                                                key={`extra-${index}`}
                                                fill={selectedMonth ? (entry.name === selectedMonth ? '#60a5fa' : '#3b82f640') : '#3b82f6'}
                                                stroke={entry.name === selectedMonth ? '#fff' : 'none'}
                                                strokeWidth={entry.name === selectedMonth ? 2 : 0}
                                                style={{ transition: 'all 0.3s ease', filter: entry.name === selectedMonth ? 'drop-shadow(0 0 8px rgba(96, 165, 250, 0.6))' : 'none' }}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        {/* Tap hint */}
                        <p className={`text-center text-[10px] mt-2 transition-opacity duration-300 ${selectedMonth ? 'text-orange-400 font-semibold' : 'text-slate-500'}`}>
                            {selectedMonth ? `📊 Mostrando: ${selectedMonth}` : '👆 Toque em uma coluna para ver o extrato do mês'}
                        </p>
                    </div>

                    <div id="tour-breakdown" className={`space-y-6 transition-all duration-500 ${tourStep === 7 ? 'relative z-[105] ring-4 ring-orange-500 rounded-xl bg-slate-900/50 p-2 -m-2' : ''}`}>
                        {/* BREAKDOWN */}
                        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-lg flex-grow">
                            <h3 className="text-lg font-semibold text-white mb-4">Fontes de Renda (Mês Atual)</h3>
                            <div className="space-y-4">
                                {/* Total Rhino (Base) uses currentMonthRhinoBase, which is clean from tips */}
                                <BreakdownBar label="Base Rhino + Adicionais" value={stats.currentMonthRhinoBase} total={stats.currentMonthRhinoTotal + stats.currentMonthExtra} color="bg-orange-600" icon={Sun} subLabel="Fixo + Noturno + Bilíngue" />
                                <BreakdownBar label="Gorjetas (Tips)" value={stats.currentMonthTips} total={stats.currentMonthRhinoTotal + stats.currentMonthExtra} color="bg-yellow-500" icon={Coins} />
                                <BreakdownBar label="Renda Extra (Apps/Vendas)" value={stats.currentMonthExtra} total={stats.currentMonthRhinoTotal + stats.currentMonthExtra} color="bg-blue-500" icon={TrendingUp} />

                                <div className="pt-4 border-t border-slate-700/50">
                                    <BreakdownBar
                                        label="Taxas de Adiantamento"
                                        value={stats.currentMonthFees}
                                        total={stats.currentMonthExpenses}
                                        color="bg-red-400"
                                        icon={Percent}
                                        subLabel="Custo Financeiro Plific"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* HOURS BREAKDOWN */}
                        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-lg">
                            <h3 className="text-lg font-semibold text-white mb-4">Distribuição de Horas</h3>
                            <div className="space-y-4">
                                <BreakdownBar
                                    label="Horas Diurnas"
                                    value={stats.currentMonthDayHours}
                                    total={stats.currentMonthDayHours + stats.currentMonthNightHours}
                                    color="bg-yellow-500"
                                    icon={Sun}
                                    valueFormatter={(v: number) => `${v.toFixed(1)} h`}
                                    subLabel="Base Normal"
                                />
                                <BreakdownBar
                                    label="Horas Noturnas"
                                    value={stats.currentMonthNightHours}
                                    total={stats.currentMonthDayHours + stats.currentMonthNightHours}
                                    color="bg-purple-500"
                                    icon={Moon}
                                    valueFormatter={(v: number) => `${v.toFixed(1)} h`}
                                    subLabel="Adicional Noturno Aplicado"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* DETAILS LIST */}
                <div className="lg:col-span-1" id="month-details-container">
                    {selectedMonth ? (
                        <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg flex flex-col animate-fade-in relative mt-4 lg:mt-0 xl:mt-0">
                            <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-800/80 rounded-t-xl sticky top-0 z-10 backdrop-blur-md">
                                <div><h3 className="font-bold text-white text-lg">{selectedMonth}</h3><p className="text-xs text-slate-400">{selectedShifts.length} registros</p></div>
                                <button onClick={() => setSelectedMonth(null)} className="p-2 hover:bg-slate-700 rounded-full transition-colors text-slate-400 hover:text-white bg-slate-900/50"><X className="w-5 h-5" /></button>
                            </div>
                            <div className="p-3 space-y-3 max-h-[60vh] lg:max-h-[700px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
                                {selectedShifts.map((shift) => {
                                    const isThrifty = isThriftyDay(shift);
                                    const expenses = shift.expenses ? shift.expenses.reduce((a, b) => a + b.amount, 0) : 0;
                                    const extras = shift.extraEarnings ? shift.extraEarnings.reduce((a, b) => a + b.amount, 0) : 0;
                                    const net = ((shift.totalEarnings || 0) + (shift.tips || 0) + extras) - expenses;

                                    return (
                                        <div key={shift.id} className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50 hover:border-orange-500/30 transition-colors flex flex-col gap-2 group relative overflow-hidden">
                                            {isThrifty && shift.type === 'WORK' && <div className="absolute top-0 right-0 bg-green-500/20 px-2 py-1 rounded-bl-xl"><PiggyBank className="w-4 h-4 text-green-400" /></div>}

                                            <div className="flex justify-between items-start">
                                                <div className="flex items-center gap-3">
                                                    {shift.type === 'OFF' && <Coffee className="w-5 h-5 text-slate-500" />}
                                                    {shift.type === 'NO_SHOW' && <AlertTriangle className="w-5 h-5 text-red-500" />}
                                                    <div>
                                                        <p className="text-base font-medium text-slate-200">{format(parseDate(shift.date), "dd 'de' MMM", { locale: ptBR })}</p>
                                                        <span className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                                                            {shift.type === 'WORK' ? <><Clock className="w-3 h-3" /> {shift.totalHours}h</> : <span className="italic">{shift.type === 'OFF' ? 'Folga' : 'Falta'}</span>}
                                                        </span>
                                                    </div>
                                                </div>
                                                {shift.type === 'WORK' && (
                                                    <div className="text-right">
                                                        <span className="font-bold text-blue-400 text-base block">Liq: {formatValue(net)}</span>
                                                        {expenses > 0 && <span className="text-xs text-red-400 block mt-1">- {formatValue(expenses)} gastos</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="hidden lg:flex bg-slate-800/50 border border-dashed border-slate-700 rounded-xl h-full flex-col items-center justify-center p-6 text-center text-slate-500 min-h-[300px]">
                            <div className="bg-slate-800 p-3 rounded-full mb-3"><ChevronRight className="w-6 h-6 text-slate-600" /></div>
                            <p className="text-sm">Selecione um mês no gráfico para visualizar o extrato detalhado.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* REPORT MODAL */}
            {showReportModal && (
                <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-2xl max-w-sm w-full">
                        <div className="flex justify-between items-center mb-6 border-b border-slate-700 pb-2">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2"><FileText className="w-5 h-5 text-green-500" /> Gerar Espelho RH</h3>
                            <button onClick={() => setShowReportModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="report_month" className="block text-sm font-medium text-slate-400 mb-2">Selecione o Mês</label>
                                <select
                                    id="report_month"
                                    name="month"
                                    value={reportMonth}
                                    onChange={(e) => setReportMonth(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                >
                                    {availableMonths.map(m => {
                                        const [y, monthIdx] = m.split('-');
                                        const date = new Date(parseInt(y), parseInt(monthIdx) - 1);
                                        const label = format(date, 'MMMM yyyy', { locale: ptBR });
                                        return <option key={m} value={m}>{label.charAt(0).toUpperCase() + label.slice(1)}</option>;
                                    })}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="report_app_tips" className="block text-sm font-medium text-slate-400 mb-2">Gorj. App (Plific)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-3.5 text-slate-500 text-xs">R$</span>
                                        <input
                                            id="report_app_tips"
                                            name="app_tips"
                                            type="number"
                                            step="0.01"
                                            value={reportAppTipsManual}
                                            onChange={(e) => setReportAppTipsManual(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-3 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="report_discount" className="block text-sm font-medium text-slate-400 mb-2">Multas/Descontos</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-3.5 text-slate-500 text-xs">R$</span>
                                        <input
                                            id="report_discount"
                                            name="discount"
                                            type="number"
                                            step="0.01"
                                            value={reportManualDiscount}
                                            onChange={(e) => setReportManualDiscount(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-8 pr-3 py-3 text-white focus:ring-2 focus:ring-red-500 outline-none text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-900/30 p-4 rounded-xl border border-slate-700">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-6 rounded-full transition-colors relative cursor-pointer ${reportBonusToggle ? 'bg-orange-600' : 'bg-slate-600'}`} onClick={() => setReportBonusToggle(!reportBonusToggle)}>
                                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${reportBonusToggle ? 'left-5' : 'left-1'}`} />
                                        </div>
                                        <span className="text-sm font-medium text-slate-300">Incluir Bônus (10%)</span>
                                    </div>
                                    <Percent className={`w-5 h-5 ${reportBonusToggle ? 'text-orange-500' : 'text-slate-600'}`} />
                                </div>
                                <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                                    ⚠️ O bônus será automaticamente removido se houver 2+ atrasos acima de 10min no mês ou faltas (No-show).
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-8">
                            <button
                                onClick={() => setShowReportModal(false)}
                                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-xl transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleGenerateMirror}
                                disabled={isSharingReport}
                                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-lg"
                            >
                                {isSharingReport ? <Loader2 className="w-5 h-5 animate-spin" /> : <Copy className="w-5 h-5" />}
                                Gerar e Compartilhar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};